import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, UserCheck, User, ArrowRight, ArrowLeft, Calendar, Sparkles, Activity, AlertTriangle } from 'lucide-react';
import { getCurrentUserRole } from '../../utils/userRoleCheck';
import './AuthGateModal.css';

/**
 * AuthGateModal - shown when an user clicks "Book Now".
 */
const AuthGateModal = ({ open, onClose, redirectTo, onGuest }) => {
  const navigate = useNavigate();
  const [step, setStep] = React.useState('initial'); // 'initial' | 'select-type'
  const userRole = getCurrentUserRole();

  React.useEffect(() => {
    if (!open) {
      setStep('initial');
    }
  }, [open]);

  const handleHaveAccount = () => {
    onClose();
    navigate('/portal/login', {
      state: { from: redirectTo || '/book' }
    });
  };

  const handleGuest = () => {
    const hasSelectedTest = redirectTo && (redirectTo.includes('test=') || redirectTo.includes('selectedTestId') || redirectTo.includes('cart'));
    if (hasSelectedTest) {
      onClose();
      if (onGuest) {
        onGuest();
      } else {
        navigate(redirectTo);
      }
    } else {
      setStep('select-type');
    }
  };

  const handleBack = () => {
    if (step === 'select-type') {
      setStep('initial');
    }
  };

  const handleSelectType = (type) => {
    if (type === 'collection') {
      onClose();
      navigate('/book');
    } else if (type === 'test' || type === 'both') {
      onClose();
      navigate('/tests');
    }
  };

  const handleSwitchToPatient = () => {
    // Logout staff token and redirect to patient login
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    localStorage.removeItem('phleb_token');
    localStorage.removeItem('phleb_user');
    localStorage.removeItem('employer_token');
    localStorage.removeItem('employer_user');
    localStorage.removeItem('hub_token');
    localStorage.removeItem('hub_user');
    localStorage.removeItem('diag_token');
    localStorage.removeItem('diag_user');
    localStorage.removeItem('res_token');
    localStorage.removeItem('res_user');
    onClose();
    navigate('/portal/login', { state: { from: redirectTo || '/book' } });
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="agm-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="agm-card"
            initial={{ scale: 0.85, opacity: 0, y: 40 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.85, opacity: 0, y: 40 }}
            transition={{ type: 'spring', stiffness: 260, damping: 22 }}
            onClick={e => e.stopPropagation()}
          >
            {!userRole.isStaff && (
              <button
                className="agm-back"
                onClick={step !== 'initial' ? handleBack : onClose}
                aria-label="Go Back"
                title={step !== 'initial' ? "Go back to options" : "Go back"}
              >
                <ArrowLeft size={18} />
              </button>
            )}

            <button className="agm-close" onClick={onClose} aria-label="Close">
              <X size={18} />
            </button>

            {/* Non-Patient Staff Warning Modal */}
            {userRole.isStaff && (
              <>
                <div className="agm-icon-wrap" style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444' }}>
                  <AlertTriangle size={38} />
                </div>

                <h2 className="agm-title">Patient Account Required</h2>
                <p className="agm-subtitle" style={{ fontSize: '0.88rem', lineHeight: '1.45', margin: '0 0 1.25rem' }}>
                  You are currently logged in as a <strong>{userRole.label}</strong>. Diagnostic test bookings and mobile sample collection orders are strictly reserved for Patient accounts. Please log in with a patient account to place an order.
                </p>

                <div className="agm-actions-row">
                  <button className="agm-btn agm-btn-ghost" onClick={() => { onClose(); navigate(userRole.dashboard); }}>
                    Return to Dashboard
                  </button>
                  <button className="agm-btn agm-btn-primary" onClick={handleSwitchToPatient}>
                    <UserCheck size={16} />
                    Log In as Patient
                  </button>
                </div>
              </>
            )}

            {!userRole.isStaff && step === 'initial' && (
              <>
                <div className="agm-icon-wrap">
                  <UserCheck size={38} />
                </div>

                <h2 className="agm-title">Ready to Book?</h2>
                <p className="agm-subtitle">
                  Do you have a MusB Diagnostics account? Logging in lets us pre-fill
                  your details and saves your booking history.
                </p>

                <div className="agm-actions">
                  <button className="agm-btn agm-btn-primary" onClick={handleHaveAccount}>
                    <UserCheck size={18} />
                    Yes, Log In / Sign Up
                    <ArrowRight size={16} className="agm-arrow" />
                  </button>

                  <button className="agm-btn agm-btn-ghost" onClick={handleGuest}>
                    <User size={18} />
                    Continue as Guest
                  </button>
                </div>

                <p className="agm-note">
                  No account? No problem - you can still book as a guest.
                </p>
              </>
            )}

            {!userRole.isStaff && step === 'select-type' && (
              <>
                <div className="agm-icon-wrap">
                  <Activity size={38} />
                </div>

                <h2 className="agm-title">What do you need?</h2>
                <p className="agm-subtitle">
                  Select the service you would like to book today.
                </p>

                <div className="agm-option-cards">
                  <button className="agm-option-card" onClick={() => handleSelectType('collection')}>
                    <div className="agm-option-icon">
                      <Calendar size={22} />
                    </div>
                    <div className="agm-option-info">
                      <h3>Sample Collection Only</h3>
                      <p>Book a phlebotomist to collect your sample or walk in.</p>
                    </div>
                  </button>



                  <button className="agm-option-card" onClick={() => handleSelectType('both')}>
                    <div className="agm-option-icon">
                      <Sparkles size={22} />
                    </div>
                    <div className="agm-option-info">
                      <h3>Both (Tests & Collection)</h3>
                      <p>Select tests first, then book your collection visit.</p>
                    </div>
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default AuthGateModal;
